import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Api } from '../../services/api';
import { NavbarService } from '../../services/navbar.service';
import { SessionService } from '../../services/session.service';
import { PermissionService } from '../../services/permission.service';
import { jwtDecode } from 'jwt-decode';
import { AlertService } from '../../services/alert.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  imports: [FormsModule],
  templateUrl: './login.html',
  styleUrl: './login.css',
})
export class Login implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  public showLogin: boolean = true;
  public showRecover: boolean = false;
  private api = inject(Api);
  public router = inject(Router);
  public user: string = '';
  public pass: string = '';
  public recoverMail: string = '';
  public showMessage: boolean = false;
  public showVerification: boolean = false;
  public verificationCode: string[] = ['', '', '', '', '', ''];
  public invalidCreds: boolean = false;
  public showResetPassword: boolean = false;
  public newPassword: string = '';
  public confirmNewPassword: string = '';
  private alertService = inject(AlertService);

  constructor(
    private navbarService: NavbarService,
    private sessionService: SessionService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.navbarService.hideNavbar();
    
    if (this.authService.isAuthenticated()) {
      this.redirectAfterLogin();
    }
  }

  ngOnDestroy(): void {
    this.navbarService.displayNavbar();
  }

  userLogin() {
    this.api.showLoading();
    const data = {
      username: this.user,
      pass: this.pass,
    };

    this.api.getLogin(data).subscribe({
      next: (data: any) => {
        setTimeout(() => {
          this.api.hideLoading();
          localStorage.setItem('crmtoken', data.token);
          this.sessionService.setToken(data.token);

          const decoded: any = jwtDecode(data.token);
          this.permissionService.setPermissions(decoded.permisos);

          this.sessionService
            .loadUserData()
            .then(() => {
              console.log('✅ Login: Datos de usuario cargados, navegando...');
              this.redirectAfterLogin();
            })
            .catch((error) => {
              console.error('❌ Login: Error al cargar datos:', error);
              this.redirectAfterLogin();
            });

          console.log('✅ Login exitoso, token guardado y AuthService actualizado');
        }, 500);
      },
      error: (error: any) => {
        setTimeout(() => {
          this.api.hideLoading();
          this.invalidCreds = true;
        }, 500);
      },
    });
  }

  userRecover() {
    this.api.showLoading();
    const data = {
      email: this.recoverMail,
    };

    this.api.recoveryMail(data).subscribe((data: any) => {
      this.api.hideLoading();
      this.showMessage = true;
      this.showVerification = true;
    });
  }

  onCodeInput(index: number, event: any) {
    const value = event.target.value.toUpperCase();
    const allowedChars = /^[23456789ABCDEFGHJKMNPQRSTUVWXYZ]$/;
    
    if (value.length === 1 && allowedChars.test(value)) {
      this.verificationCode[index] = value;
      event.target.value = value;
      const nextInput = event.target.parentElement.parentElement.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) {
        nextInput.focus();
      }
    } else if (value.length === 0) {
      this.verificationCode[index] = '';
    } else {
      event.target.value = this.verificationCode[index];
    }
  }

  onCodeKeydown(index: number, event: any) {
    if (event.key === 'Backspace' && this.verificationCode[index] === '') {
      const prevInput = event.target.parentElement.parentElement.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  verifyCode() {
    const code = this.verificationCode.join('');
    if (code.length === 6) {
      console.log('Código ingresado:', code);
      console.log(this.recoverMail);
      this.api.verifyRecoveryCode({codigo: code, email: this.recoverMail}).subscribe({
        next: (data:any) =>{
          this.api.hideLoading();
          this.showResetPassword = true;
        },
        error: (error:any) =>{
          this.api.hideLoading();
          this.alertService.error('Error', 'El código de verificación es incorrecto.');
        }
      });
    } else {
      console.log('Código incompleto');
    }
  }

  resendCode() {
    this.userRecover();
  }

  cancelVerification() {
    this.showMessage = false;
    this.showVerification = false;
    this.verificationCode = ['', '', '', '', '', ''];
    this.getLogin();
  }

  getLogin() {
    this.showLogin = true;
    this.showRecover = false;
  }

  getRecover() {
    this.showLogin = false;
    this.showRecover = true;
  }

  hasMinLength(): boolean {
    return this.newPassword.length >= 8;
  }

  hasUpperCase(): boolean {
    return /[A-Z]/.test(this.newPassword);
  }

  hasNumber(): boolean {
    return /\d/.test(this.newPassword);
  }

  hasSpecialChar(): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(this.newPassword);
  }

  isPasswordValid(): boolean {
    return this.hasMinLength() && 
           this.hasUpperCase() && 
           this.hasNumber() && 
           this.hasSpecialChar() &&
           this.newPassword === this.confirmNewPassword &&
           this.newPassword !== '' &&
           this.confirmNewPassword !== '';
  }

  resetPassword() {
    if (!this.isPasswordValid()) {
      this.alertService.warning('Validación de contraseña', 'Por favor, cumple todos los requisitos de contraseña');
      return;
    }

    this.api.showLoading();
    const data = {
      email: this.recoverMail,
      codigo: this.verificationCode.join(''),
      newPassword: this.newPassword,
      confirmPassword: this.confirmNewPassword
    };

    console.log('🔄 Enviando datos de reset:', data);

    this.api.resetPassword(data).subscribe({
      next: (response: any) => {
        this.api.hideLoading();
        this.alertService.success('Contraseña actualizada', 'Tu contraseña ha sido restablecida exitosamente');
        this.resetAllStates();
        this.getLogin();
      },
      error: (error: any) => {
        this.api.hideLoading();
        this.alertService.error('Error', 'No se pudo restablecer la contraseña. Intenta nuevamente.');
      }
    });
  }

  cancelPasswordReset() {
    this.resetAllStates();
    this.getLogin();
  }

  private resetAllStates() {
    this.showMessage = false;
    this.showVerification = false;
    this.showResetPassword = false;
    this.verificationCode = ['', '', '', '', '', ''];
    this.newPassword = '';
    this.confirmNewPassword = '';
    this.recoverMail = '';
  }

  // 🔄 Método para redirigir después del login exitoso
  private redirectAfterLogin(): void {
    // Verificar si hay una ruta de retorno guardada por el AuthGuard
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    
    if (returnUrl && returnUrl !== '/login') {
      console.log(`🔄 Redirigiendo a ruta original: ${returnUrl}`);
      // Pequeño delay para permitir que AuthService se actualice
      setTimeout(() => {
        this.router.navigate([returnUrl]);
      }, 100);
    } else {
      console.log('🔄 Redirigiendo a página principal');
      // Redirigir a una ruta específica en lugar de '/'
      setTimeout(() => {
        this.router.navigate(['/home-page']);
      }, 100);
    }
  }
}
